import React, { useEffect, useState } from "react";
import { AlertTriangle, Download, Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/auth";

const HORIZONS = [["eom", "End of This Month"], ["eonm", "End of Next Month"], ["eoy", "End of Year"], ["3y", "3 Years Out"], ["5y", "5 Years Out"]];
const usd = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const num = (v) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const fmt = (r, v) => (r.kind?.startsWith("money") ? usd(v) : r.kind === "pct" ? (v == null ? "—" : `${v}%`) : num(v));
const trend = (v) => (v == null ? "" : `${v >= 0 ? "+" : ""}${num(v)}/wk`);

function Row({ r, i }) {
  const money = r.kind === "money";
  const flow = money || r.kind === "flow";
  const f = (v) => (money ? usd(v) : num(v));
  const now = flow ? f(r.to_date) : `${fmt(r, r.now)}${r.amount != null ? ` · ${usd(r.amount)}` : ""}`;
  const proj = flow ? f(r.projected) : "projected_value" in r ? fmt(r, r.projected_value) : r.projected != null ? num(r.projected) : "";
  const avg = r.weekly_avg == null ? "" : flow ? `${f(r.weekly_avg)}/wk` : fmt(r, r.weekly_avg);
  return (
    <tr className="border-t border-white/5" data-testid={`forecast-row-${i}`}>
      <td className="py-1.5 pr-3 text-nex-silver">{r.label}{r.note && <div className="text-[10px] text-nex-muted">{r.note}</div>}</td>
      <td className="py-1.5 pr-3 text-right font-mono text-white">{now}</td>
      <td className="py-1.5 pr-3 text-right font-mono text-nex-muted">{avg}</td>
      <td className="py-1.5 pr-3 text-right font-mono text-nex-muted">{trend(r.trend_per_week)}</td>
      <td className="py-1.5 text-right font-mono text-nex-gold">{proj}</td>
    </tr>
  );
}

/** Forecast Report: baseline now + projection to the chosen horizon, with gaps called out. Super admins and every partner. */
export default function ForecastReport() {
  const [h, setH] = useState("eom");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    apiClient.get("/forecast", { params: { horizon: h } }).then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e))).finally(() => setBusy(false));
  }, [h]);
  const pdf = async () => {
    setPdfBusy(true);
    try {
      const r = await apiClient.get("/forecast/pdf", { params: { horizon: h }, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `TEA-Forecast-${h}.pdf`; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); }
    setPdfBusy(false);
  };
  return (
    <section className="space-y-4" data-testid="forecast-report">
      <div className="flex flex-wrap items-center gap-2">
        <TrendingUp className="h-4 w-4 text-nex-gold" /><h3 className="font-mono text-[11px] tracking-[0.2em] uppercase text-nex-gold mr-2">Forecast Report</h3>
        {HORIZONS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setH(k)} data-testid={`forecast-h-${k}`}
            className={`px-3 py-1 rounded-full border text-[11px] transition-colors ${h === k ? "border-nex-gold bg-nex-gold/15 text-nex-gold" : "border-white/15 text-nex-muted hover:text-white"}`}>{l}</button>))}
        <button type="button" onClick={pdf} disabled={pdfBusy || !data} data-testid="forecast-pdf"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-nex-gold text-black text-xs font-semibold disabled:opacity-50">
          {pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download PDF</button>
      </div>
      {busy && !data ? <div className="text-sm text-nex-muted" data-testid="forecast-loading">Calculating…</div> : data && (
        <>
          <p className="text-[11px] text-nex-muted" data-testid="forecast-meta">As of {data.as_of} → <b className="text-white">{data.horizon_label}</b> ({data.end}, {data.weeks_ahead} weeks) · {data.method}{busy && " · updating…"}</p>
          {data.sections.map((s, si) => (
            <div key={s.title} className="border border-white/10 rounded-sm p-3" data-testid={`forecast-section-${si}`}>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted mb-1">{s.title}<span className="sm:hidden normal-case tracking-normal ml-2 text-nex-gold/70">scroll → for projections</span></div>
              <div className="overflow-x-auto"><table className="w-full text-xs min-w-[640px] table-fixed">
                <thead><tr className="text-left font-mono text-[9px] uppercase tracking-[0.16em] text-nex-muted"><th className="py-1 w-[38%]">Figure</th><th className="text-right">Now / to date</th><th className="text-right">Recent avg</th><th className="text-right">Trend</th><th className="text-right">Projected by {data.end}</th></tr></thead>
                <tbody>{s.rows.map((r, i) => <Row key={r.label} r={r} i={`${si}-${i}`} />)}</tbody>
              </table></div>
            </div>))}
          <div className="border border-amber-400/40 bg-amber-400/[0.05] rounded-sm p-3" data-testid="forecast-gaps">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-amber-300 mb-1"><AlertTriangle className="h-3.5 w-3.5" /> Gaps to fix ({data.gaps.length})</div>
            <ul className="list-disc pl-5 space-y-0.5 text-xs text-nex-silver">{data.gaps.map((g) => <li key={g}>{g}</li>)}</ul>
          </div>
        </>)}
    </section>
  );
}
